/**
 * url-guard — the single source of truth for outbound-request URL validation.
 *
 * This module centralizes the SSRF/host-allowlist control so that every network
 * client in the app (fetch via safeFetch, axios via safeAxios) validates
 * destination URLs against exactly one policy. Do not duplicate this logic in
 * individual clients — import assertUrlAllowed() instead.
 */

/**
 * Allowlist of trusted external hosts the application may call in addition to
 * its own origin. Same-origin requests (relative paths and getLocalPath()-based
 * URLs) always pass and need not be listed. Suffix-matched against the resolved
 * hostname (e.g. `ameriprise.com` matches `api.wam.qa.ameriprise.com`).
 * Keep this list minimal — it is the trust boundary for outbound requests.
 */
export const ALLOWED_EXTERNAL_HOST_SUFFIXES = Object.freeze([
  'ameriprise.com',
  'advisorcompass.com',
  'columbiathreadneedleus.com',
])

/**
 * Hostname patterns that must never be reached from client-issued requests.
 * Defense-in-depth against SSRF via IP-literal / loopback / metadata targets.
 */
export const BLOCKED_HOST_PATTERNS = Object.freeze([
  /^localhost$/i,
  /^127(?:\.\d{1,3}){3}$/, // 127.0.0.0/8 loopback
  /^0\.0\.0\.0$/,
  /^10(?:\.\d{1,3}){3}$/, // 10.0.0.0/8 private
  /^192\.168(?:\.\d{1,3}){2}$/, // 192.168.0.0/16 private
  /^172\.(?:1[6-9]|2\d|3[01])(?:\.\d{1,3}){2}$/, // 172.16.0.0/12 private
  /^169\.254(?:\.\d{1,3}){2}$/, // link-local / cloud metadata
  /^\[?::1\]?$/, // IPv6 loopback
])

/**
 * Returns the current page origin, or undefined outside a browser context.
 * @returns {string|undefined}
 */
export function getCurrentOrigin() {
  return (
    (typeof globalThis !== 'undefined' &&
      globalThis.location &&
      globalThis.location.origin) ||
    undefined
  )
}

// Decides whether a resolved target host may be contacted.
export function isHostAllowed(hostname, isSameOrigin) {
  const host = hostname.toLowerCase()

  // Never permit loopback / private / metadata targets, even same-origin,
  // unless the page itself is legitimately served from localhost (local dev).
  const runningOnLocalhost =
    typeof globalThis !== 'undefined' &&
    globalThis.location &&
    /^localhost$|^127\./i.test(globalThis.location.hostname)

  if (
    !runningOnLocalhost &&
    BLOCKED_HOST_PATTERNS.some((re) => re.test(host))
  ) {
    return false
  }

  if (isSameOrigin) {
    return true
  }

  return ALLOWED_EXTERNAL_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`)
  )
}

/**
 * Validates a request target and returns the fully-resolved URL string to use
 * for the request. Throws when the URL fails validation. This is the single
 * choke point every network client must call before issuing a request.
 */
export function assertUrlAllowed(url) {
  const base = getCurrentOrigin()

  let resolved
  try {
    resolved = base ? new URL(url, base) : new URL(url)
  } catch {
    throw new Error('Blocked request: malformed URL')
  }

  if (resolved.protocol !== 'https:' && resolved.protocol !== 'http:') {
    throw new Error(
      `Blocked request: unsupported protocol "${resolved.protocol}"`
    )
  }

  const isSameOrigin = base ? resolved.origin === base : false

  if (!isHostAllowed(resolved.hostname, isSameOrigin)) {
    throw new Error('Blocked request: destination host is not allowed')
  }

  return resolved.href
}
